/**
 * Centralized path normalization utility
 * Converts Unix-style paths to Windows paths
 * 
 * This utility provides consistent path handling across all components
 * to avoid path normalization inconsistencies.
 */

/**
 * Normalizes a path to Windows format
 * Handles various input formats:
 * - Unix-style paths: /Users/John/Downloads
 * - Windows paths: C:\Users\John\Downloads
 * - Drive letters: C:, E:
 * - Special paths: This PC, /E:, etc.
 * 
 * @param {string} inputPath - The path to normalize
 * @returns {string} Normalized Windows path
 */
export const normalizePath = (inputPath = '') => {
  if (typeof inputPath !== 'string') {
    return '';
  }

  // Remove trailing slashes and normalize separators
  let path = inputPath.replace(/\\\\/g, '\\').replace(/\//g, '\\').trim();

  if (!path) {
    return '';
  }

  // Handle "This PC" special path
  if (path.startsWith('This PC')) {
    return path.replace(/\\\\/g, '\\').replace(/\//g, '\\');
  }

  // Handle drive letters (C:, E:, etc.)
  if (/^[A-Za-z]:$/.test(path)) {
    return `${path}\\`;
  }

  // Handle paths starting with drive letters (C:\, E:\, etc.)
  if (/^[A-Za-z]:\\/.test(path)) {
    // Remove trailing slashes for non-root paths
    if (path.length > 3 && path.endsWith('\\')) {
      return path.slice(0, -1);
    }
    return path;
  }

  // Handle removable drives (E:)
  if (path.startsWith('/E:') || path.startsWith('E:')) {
    const normalized = 'E:' + path.replace(/^\/?E:/, '').replace(/\//g, '\\');
    // Remove trailing slashes for non-root paths
    if (normalized.length > 3 && normalized.endsWith('\\')) {
      return normalized.slice(0, -1);
    }
    return normalized;
  }

  // Handle Program Files and other system paths
  if (path.includes('Program Files') || path.includes('Windows')) {
    // If it starts with a backslash, prepend C:
    if (path.startsWith('\\')) {
      path = `C:${path}`;
    } else if (!path.startsWith('C:')) {
      path = `C:\\${path}`;
    }
    // Remove trailing slashes for non-root paths
    if (path.length > 3 && path.endsWith('\\')) {
      return path.slice(0, -1);
    }
    return path;
  }

  // Default: assume user's home directory
  // Remove leading slashes/backslashes
  const cleanedPath = path.replace(/^[\\\/]+/, '');

  // Handle paths that already start with Users\...
  const usersMatch = cleanedPath.match(/^Users\\(.+)/i);
  if (usersMatch) {
    const usersPath = `C:\\Users\\${usersMatch[1]}`.replace(/\\+/g, '\\');
    if (usersPath.length > 3 && usersPath.endsWith('\\')) {
      return usersPath.slice(0, -1);
    }
    return usersPath;
  }

  // Default to user's home directory
  const basePath = 'C:\\Users\\John Anderson';
  const normalized = `${basePath}\\${cleanedPath}`.replace(/\\+/g, '\\');

  // Remove trailing slashes for non-root paths
  if (normalized.length > basePath.length + 1 && normalized.endsWith('\\')) {
    return normalized.slice(0, -1);
  }

  return normalized;
};

/**
 * Get parent path from a given path
 * 
 * @param {string} path - The path to get the parent of
 * @returns {string} Parent path, or 'This PC' if root
 */
export const getParentPath = (path) => {
  const normalized = normalizePath(path);
  
  if (!normalized || normalized === 'This PC') {
    return 'This PC';
  }

  // Handle drive roots (C:\, E:\, etc.)
  if (/^[A-Za-z]:\\?$/.test(normalized)) {
    return 'This PC';
  }

  const lastIndex = normalized.lastIndexOf('\\');
  
  if (lastIndex === -1) {
    return 'This PC';
  }

  // Handle paths like C:\ (root of drive)
  if (lastIndex <= 2) {
    const driveRoot = normalized.slice(0, Math.max(lastIndex + 1, 3));
    return driveRoot.endsWith('\\') ? driveRoot : `${driveRoot}\\`;
  }

  return normalized.slice(0, lastIndex);
};

/**
 * Join path segments into a Windows path
 * 
 * @param {...string} segments - Path segments to join
 * @returns {string} Joined Windows path
 */
export const joinPath = (...segments) => {
  if (segments.length === 0) {
    return '';
  }

  // Filter out empty segments
  const validSegments = segments.filter(seg => seg && typeof seg === 'string');

  if (validSegments.length === 0) {
    return '';
  }

  // Join segments and normalize
  const joined = validSegments
    .map(seg => seg.replace(/^[\\\/]+|[\\\/]+$/g, '')) // Remove leading/trailing slashes
    .filter(seg => seg.length > 0) // Filter empty segments
    .join('\\');

  // If first segment is a drive letter, ensure it has a backslash
  if (/^[A-Za-z]:$/.test(validSegments[0])) {
    return `${validSegments[0]}\\${joined}`;
  }

  // If first segment starts with a drive letter and backslash, use it as-is
  if (/^[A-Za-z]:\\/.test(validSegments[0])) {
    return `${validSegments[0]}\\${joined}`;
  }

  return normalizePath(joined);
};

/**
 * Get the file name from a path
 * 
 * @param {string} path - The path to extract the file name from
 * @returns {string} File name, or empty string if not found
 */
export const getFileName = (path) => {
  const normalized = normalizePath(path);
  
  if (!normalized) {
    return '';
  }

  const lastIndex = normalized.lastIndexOf('\\');
  
  if (lastIndex === -1) {
    return normalized;
  }

  return normalized.slice(lastIndex + 1);
};

/**
 * Get the directory path from a file path
 * 
 * @param {string} path - The file path
 * @returns {string} Directory path
 */
export const getDirectoryPath = (path) => {
  const normalized = normalizePath(path);
  return getParentPath(normalized);
};

/**
 * Check if a path is a root path (This PC or drive root)
 * 
 * @param {string} path - The path to check
 * @returns {boolean} True if the path is a root path
 */
export const isRootPath = (path) => {
  const normalized = normalizePath(path);
  return normalized === 'This PC' || /^[A-Za-z]:\\?$/.test(normalized);
};

/**
 * Normalize a directory path (ensures it ends with backslash for directories)
 * 
 * @param {string} path - The path to normalize as a directory
 * @returns {string} Normalized directory path
 */
export const normalizeDirectory = (path) => {
  const normalized = normalizePath(path);
  
  // Don't add trailing slash to "This PC"
  if (normalized === 'This PC') {
    return normalized;
  }

  // Drive roots should end with backslash
  if (/^[A-Za-z]:$/.test(normalized)) {
    return `${normalized}\\`;
  }

  // If it's already a drive root with backslash, return as-is
  if (/^[A-Za-z]:\\$/.test(normalized)) {
    return normalized;
  }

  // For other paths, don't add trailing slash (let the component decide)
  return normalized;
};
